import { useEffect, useRef, useState } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import mermaid from 'mermaid';

mermaid.initialize({ startOnLoad: false, securityLevel: 'strict', suppressErrorRendering: true });

export const MERMAID_TYPE = 'mermaid';

export const PartialMermaidRenderer = ({
    source,
    theme,
    language,
}: {
    source: string;
    language: string;
    theme: { [key: string]: React.CSSProperties };
}) => {
    const mermaidRef = useRef<HTMLDivElement>(null);
    const [shouldUseFallback, setShouldUseFallback] = useState(false);

    useEffect(() => {
        const renderMermaid = async (source: string) => {
            if (mermaidRef.current) {
                try {
                    const id = `mermaid-diagram-${Math.random().toString(36).substring(2, 11)}`;
                    const { svg } = await mermaid.render(id, source);
                    mermaidRef.current.innerHTML = svg;
                } catch (e) {}
            }
        };

        const tryRender = async () => {
            if (mermaidRef.current) {
                const parseResult = await mermaid.parse(source, {
                    suppressErrors: true,
                });
                if (parseResult) {
                    setShouldUseFallback(false);
                    await renderMermaid(source);
                    return;
                }
                const lineList = source?.split('\n') ?? [];
                // Try to parse the source again, excluding the last two lines - this should handle SSE partial responses
                if (lineList.length < 1) {
                    setShouldUseFallback(true);
                    return;
                }
                // Trying to parse the source again, excluding the last two lines
                const trimmedValue = lineList.slice(0, -2).join('\n') ?? '';
                const parseResult2 = await mermaid.parse(trimmedValue, {
                    suppressErrors: true,
                });
                if (!parseResult2) {
                    setShouldUseFallback(true);
                    return;
                }
                setShouldUseFallback(false);
                await renderMermaid(trimmedValue);
            }
        };
        tryRender();
    }, [source]);

    return (
        <>
            <div ref={mermaidRef}>
                <span></span>
            </div>
            {shouldUseFallback && (
                <SyntaxHighlighter style={theme} language={language}>
                    {source}
                </SyntaxHighlighter>
            )}
        </>
    );
};
